import type { ReactNode } from 'react';
import { formatarFracaoPercentual } from '@/lib/format';
import { cn } from '@/lib/utils';
import { COR_FAIXA, type Faixa } from './tipos';

export interface BarraMargemProps {
  /** Rotulo da linha (link para o orcamento, mes...). */
  rotulo: ReactNode;
  /** Lucro como fracao da API ("0.1263"): so o texto. */
  lucro: string;
  /** Largura da barra: fracao 0..1 da API (CSS resolve `calc(fracao * 100%)`). */
  barra: string;
  faixa: Faixa | null;
  /** Posicao (fracao 0..1 da API) das linhas de minimo e meta; null = nao desenha. */
  posicaoMinimo?: string | null;
  posicaoMeta?: string | null;
  titulo?: string;
  /** MC% do orcamento (fracao da API), mostrada pequena ao lado do lucro. Ausente = nao mostra. */
  mcPct?: string | null;
}

/** Percentual de eixo: 5,00% vira 5%. So texto. */
const eixo = (f: string) => formatarFracaoPercentual(f).replace(/,00%$/, '%');
const cssFracao = (f: string | null | undefined) => (f && /^\d+(\.\d+)?$/.test(f) ? f : null);

/** Uma linha do grafico "Lucro por orcamento" (docs/design/Margens.dc.html): rotulo, trilho com barra e marcas, percentual. */
export function BarraMargem({ rotulo, lucro, barra, faixa, posicaoMinimo, posicaoMeta, titulo, mcPct }: BarraMargemProps) {
  const larg = cssFracao(barra);
  const min = cssFracao(posicaoMinimo);
  const meta = cssFracao(posicaoMeta);
  return (
    <div className="grid grid-cols-[70px_minmax(0,1fr)_64px] items-center gap-2.5" title={titulo}>
      <span className="tabular text-small">{rotulo}</span>
      <div className="relative h-[18px] rounded-md bg-line-soft">
        {larg && <div className={cn('absolute inset-y-0 left-0 rounded-md', faixa ? COR_FAIXA[faixa] : 'bg-ink-disabled')} style={{ width: `calc(${larg} * 100%)` }} />}
        {min && <div aria-hidden="true" className="absolute -top-[3px] -bottom-[3px] w-px bg-destructive" style={{ left: `calc(${min} * 100%)` }} />}
        {meta && <div aria-hidden="true" className="absolute -top-[3px] -bottom-[3px] w-px bg-success-ink" style={{ left: `calc(${meta} * 100%)` }} />}
      </div>
      <span className="flex flex-col items-end leading-tight">
        <span className="tabular text-small font-semibold">{formatarFracaoPercentual(lucro)}</span>
        {mcPct !== undefined && <span className="tabular text-micro text-ink-muted">MC {formatarFracaoPercentual(mcPct)}</span>}
      </span>
    </div>
  );
}

export interface EixoMargemProps {
  maximo: string;
  minimo?: { valor: string; posicao: string } | null;
  meta?: { valor: string; posicao: string } | null;
}

/** Eixo sob as barras: 0, minimo, meta (quando comuns as linhas) e o maximo da escala. */
export function EixoMargem({ maximo, minimo, meta }: EixoMargemProps) {
  const marca = (m: { valor: string; posicao: string }) => (
    <span key={m.valor} className="absolute -translate-x-1/2" style={{ left: `calc(${cssFracao(m.posicao) ?? '0'} * 100%)` }}>
      {eixo(m.valor)}
    </span>
  );
  return (
    <div aria-hidden="true" className="mt-1.5 grid grid-cols-[70px_minmax(0,1fr)_64px] gap-2.5 font-mono text-micro text-ink-muted">
      <span />
      <div className="relative h-3.5">
        <span className="absolute left-0">0%</span>
        {minimo && marca(minimo)}
        {meta && marca(meta)}
        <span className="absolute right-0">{eixo(maximo)}</span>
      </div>
      <span />
    </div>
  );
}
